import {
    GetPaperPlanMetadataOutput,
    GetWeightsProfilesOutput,
    PaperPlanMetadata,
    PerformanceDiscountsSpatialPlaysEnum,
    RatchetDefinitionsEnum,
    RatchetModelsEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { ColumnState } from 'ag-grid-community';
import { PlanWeightsTab, Service } from 'src/constants';
import { IPlanRowData, IPlanValues } from 'src/types/IPaperPlan';
import { IWeightsProfileRowData } from 'src/types/IWeightsProfile';
import { getPlanGroup, mapPaperPlanMetadataToPlanValues } from 'src/util/PaperPlans/PaperPlanUtils';

import { basePlanWeightsColDefs } from './planWeightsColDefs';

// Entity names are not exactly as stored in DB, so we will map the majors to the respective entityIds
export const PLAN_WEIGHTS_TABS = [
    { name: PlanWeightsTab.WMG, entityId: 423 },
    { name: PlanWeightsTab.UMG, entityId: 736 },
    { name: PlanWeightsTab.SONY, entityId: 195 },
    { name: PlanWeightsTab.ORCHARD, entityId: 684 },
    { name: PlanWeightsTab.SONY_JP, entityId: 775 }
];

export const PLAN_WEIGHTS_SORT_MODEL: ColumnState[] = [{ colId: 'planId', sort: 'asc', sortIndex: 0 }];

export const getColDefs = (vendorName: PlanWeightsTab, services: Service[]) => {
    return basePlanWeightsColDefs.filter((def) => {
        return def.tabs.includes(vendorName) && def.services.findIndex((svc) => services.includes(svc)) !== -1;
    });
};

export const mapPaperPlanMetadataResponseToMap = (response: GetPaperPlanMetadataOutput): Map<number, IPlanRowData> => {
    const paperPlans =
        response.planMetadata?.map(
            (paperPlanMetadata: PaperPlanMetadata) =>
                mapPaperPlanMetadataToPlanValues(paperPlanMetadata) as IPlanRowData
        ) ?? [];

    return new Map(paperPlans.map((plan) => [plan.paperPlanId!, plan]));
};

export const mapWeightsProfilesResponseToRowData = (
    response: GetWeightsProfilesOutput,
    paperPlanMetadataMap: Map<number, IPlanRowData>
): IWeightsProfileRowData[] => {
    const result: IWeightsProfileRowData[] = [];

    response.weightsProfiles?.forEach((entity) => {
        entity.planWeightsGroups?.forEach((weightGroup) => {
            const entityId = entity.entityId;
            const planId = weightGroup.paperPlanId;
            const metadata = paperPlanMetadataMap.get(planId);

            // Do not display row if metadata does not exist
            if (metadata !== undefined) {
                const planName = metadata.paperPlanName;
                const planGroup = getPlanGroup(metadata as IPlanValues);
                const planType = metadata.planType;
                const isPromotion = metadata.isPromotion;
                const customerPays = metadata.customerPays;
                const royaltyBearing = metadata.royaltyBearing;

                // TODO Prime primary/secondary and AMF conversions not included in API response yet

                let amuAlexaPsmNumerator: number | undefined;
                let amuAlexaPsmDenominator: number | undefined;
                let amuBlendedPsm: number | undefined;

                let amuDiscount1PreSpatial: number | undefined;
                let amuDiscount2PreSpatial: number | undefined;
                let amuDiscount1PostSpatial: number | undefined;
                let amuDiscount2PostSpatial: number | undefined;
                let amuDiscount3: number | undefined;
                let amuDiscount4Ft: number | undefined;
                let amuDiscount4Amu: number | undefined;
                let primePrimaryConversionNumerator: number | undefined;
                let primePrimaryConversionDenominator: number | undefined;
                let primeSecondaryConversionNumerator: number | undefined;
                let primeSecondaryConversionDenominator: number | undefined;
                let amfConversionNumerator: number | undefined;
                let amfConversionDenominator: number | undefined;

                weightGroup.paperPlanWeights?.forEach((weight) => {
                    // Alexa PSM
                    if (weight.name.startsWith('AMU_ALEXA_PSM')) {
                        if (weight.type === RatchetDefinitionsEnum.NUMERATOR) {
                            amuAlexaPsmNumerator = weight.value;
                        } else if (weight.type === RatchetDefinitionsEnum.DENOMINATOR) {
                            amuAlexaPsmDenominator = weight.value;
                        }
                    }

                    // Blended PSM
                    if (weight.name.includes('blended floor PSM')) {
                        amuBlendedPsm = weight.value;
                    }

                    // UMG Performance discount
                    if (weight.name === PerformanceDiscountsSpatialPlaysEnum.TIER_1_PRE_SPATIAL_PLAYS) {
                        amuDiscount1PreSpatial = weight.value;
                    } else if (weight.name === PerformanceDiscountsSpatialPlaysEnum.TIER_2_PRE_SPATIAL_PLAYS) {
                        amuDiscount2PreSpatial = weight.value;
                    } else if (weight.name === PerformanceDiscountsSpatialPlaysEnum.TIER_1_POST_SPATIAL_PLAYS) {
                        amuDiscount1PostSpatial = weight.value;
                    } else if (weight.name === PerformanceDiscountsSpatialPlaysEnum.TIER_2_POST_SPATIAL_PLAYS) {
                        amuDiscount2PostSpatial = weight.value;
                    } else if (weight.name === 'plan weights for performance discount 3') {
                        amuDiscount3 = weight.value;
                    } else if (weight.name === 'FT plan weights for performance discount 4') {
                        amuDiscount4Ft = weight.value;
                    } else if (weight.name === 'AMU plan weights for performance discount 4') {
                        amuDiscount4Amu = weight.value;
                    }

                    // Prime Primary Conversion
                    if (
                        (
                            [
                                RatchetModelsEnum.PRIME_CONVERSION_UMG,
                                RatchetModelsEnum.PRIME_CONVERSION_SONY,
                                RatchetModelsEnum.PRIME_CONVERSION_ORCHARD,
                                RatchetModelsEnum.PRIME_PRIMARY_CONVERSION_WMG
                            ] as string[]
                        ).includes(weight.name)
                    ) {
                        if (weight.type === RatchetDefinitionsEnum.NUMERATOR) {
                            primePrimaryConversionNumerator = weight.value;
                        } else if (weight.type === RatchetDefinitionsEnum.DENOMINATOR) {
                            primePrimaryConversionDenominator = weight.value;
                        }
                    }

                    // Prime Secondary Conversion
                    if (weight.name === RatchetModelsEnum.PRIME_SECONDARY_CONVERSION_WMG) {
                        if (weight.type === RatchetDefinitionsEnum.NUMERATOR) {
                            primeSecondaryConversionNumerator = weight.value;
                        } else if (weight.type === RatchetDefinitionsEnum.DENOMINATOR) {
                            primeSecondaryConversionDenominator = weight.value;
                        }
                    }

                    // AMF Conversion
                    if (
                        (
                            [
                                RatchetModelsEnum.AMF_CONVERSION_UMG,
                                RatchetModelsEnum.AMF_CONVERSION_WMG,
                                RatchetModelsEnum.AMF_CONVERSION_SONY,
                                RatchetModelsEnum.AMF_CONVERSION_ORCHARD
                            ] as string[]
                        ).includes(weight.name)
                    ) {
                        if (weight.type === RatchetDefinitionsEnum.NUMERATOR) {
                            amfConversionNumerator = weight.value;
                        } else if (weight.type === RatchetDefinitionsEnum.DENOMINATOR) {
                            amfConversionDenominator = weight.value;
                        }
                    }
                });

                const row: IWeightsProfileRowData = {
                    entityId: entityId,
                    planId: planName,
                    planGroup: planGroup,
                    planType: planType,
                    isPromotion: isPromotion,
                    customerPays: customerPays,
                    royaltyBearing: royaltyBearing,
                    amuAlexaPsmNumerator: amuAlexaPsmNumerator?.toString(),
                    amuAlexaPsmDenominator: amuAlexaPsmDenominator?.toString(),
                    amuBlendedPsm: amuBlendedPsm?.toString(),
                    amuDiscount1PreSpatial: amuDiscount1PreSpatial?.toString(),
                    amuDiscount2PreSpatial: amuDiscount2PreSpatial?.toString(),
                    amuDiscount1PostSpatial: amuDiscount1PostSpatial?.toString(),
                    amuDiscount2PostSpatial: amuDiscount2PostSpatial?.toString(),
                    amuDiscount3: amuDiscount3?.toString(),
                    amuDiscount4Ft: amuDiscount4Ft?.toString(),
                    amuDiscount4Amu: amuDiscount4Amu?.toString(),
                    primePrimaryNumerator: primePrimaryConversionNumerator?.toString(),
                    primePrimaryDenominator: primePrimaryConversionDenominator?.toString(),
                    primeSecondaryNumerator: primeSecondaryConversionNumerator?.toString(),
                    primeSecondaryDenominator: primeSecondaryConversionDenominator?.toString(),
                    amfConversionNumerator: amfConversionNumerator?.toString(),
                    amfConversionDenominator: amfConversionDenominator?.toString()
                };

                result.push(row);
            }
        });
    });

    return result;
};
